import { DOCUMENT, isPlatformBrowser } from "@angular/common";
import { DestroyRef, inject, Injectable, PLATFORM_ID } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import {
  catchError,
  concatMap,
  defer,
  EMPTY,
  retry,
  skip,
  Subject,
  throwError,
  timer,
} from "rxjs";
import { AttributionContextService } from "./attribution-context.service";
import { MarketingApiService } from "./marketing-api.service";
import { MarketingMeasurementService } from "./measurement.service";

type Stage = "event_viewed" | "cart_item_added" | "checkout_started";
interface Observation {
  eventId: number;
  type: Stage;
  id: string;
  receipt: string;
  generation: number;
}

@Injectable({ providedIn: "root" })
export class MarketingJourneyService {
  private readonly api = inject(MarketingApiService);
  private readonly context = inject(AttributionContextService);
  private readonly measurement = inject(MarketingMeasurementService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly browser = isPlatformBrowser(inject(PLATFORM_ID));
  private readonly document = inject(DOCUMENT);
  private generation = 0;
  private activeEvent?: number;
  private pending: Observation[] = [];
  private readonly stream = new Subject<Observation>();
  private readonly views = new Set<string>();
  private waiting: { eventId: number; type: Stage }[] = [];
  private hadSession = false;

  constructor() {
    this.stream
      .pipe(
        concatMap((observation) =>
          defer(() => {
            const token = this.context.tokenFor(observation.eventId);
            if (!token || !this.allowed(observation)) return EMPTY;
            const body = {
              event_id: observation.eventId,
              event_type: observation.type,
              context_token: token,
              consent_receipt: observation.receipt,
              event_idempotency_key: observation.id,
            };
            return defer(() =>
              this.allowed(observation) ? this.api.journey(body) : EMPTY,
            ).pipe(
              retry({
                count: 2,
                delay: (error: { status?: number }) =>
                  error.status === 0 ||
                  (error.status !== undefined && error.status >= 500)
                    ? timer(1000)
                    : throwError(() => error),
              }),
              catchError(() => EMPTY),
            );
          }),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
    this.measurement.changes
      .pipe(skip(1), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (this.hadSession || !this.measurement.analytics) {
          this.generation++;
          this.pending = [];
          this.views.clear();
        }
        const waiting = this.waiting;
        this.waiting = [];
        if (this.measurement.analytics) {
          this.hadSession = true;
          for (const entry of waiting) this.record(entry.eventId, entry.type);
        }
      });
    this.context.ready
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((eventId) => {
        if (
          this.activeEvent === eventId &&
          !this.pending.some(
            (entry) =>
              entry.eventId === eventId && entry.type === "event_viewed",
          )
        )
          this.record(eventId, "event_viewed");
        this.flush(eventId);
      });
  }
  record(eventId: number, type: Stage): void {
    if (!this.browser || !Number.isSafeInteger(eventId) || eventId <= 0) return;
    this.measurement.ensure();
    if (type === "event_viewed") this.activeEvent = eventId;
    const receipt = this.measurement.trackingReceipt;
    if (!this.measurement.analytics || !receipt) {
      if (
        this.measurement.pending &&
        this.waiting.length < 100 &&
        (type !== "event_viewed" ||
          !this.waiting.some(
            (entry) => entry.eventId === eventId && entry.type === type,
          ))
      )
        this.waiting.push({ eventId, type });
      return;
    }
    const token = this.context.tokenFor(eventId);
    if (type === "event_viewed" && token) {
      const key = `${receipt}:${eventId}:${token}`;
      if (this.views.has(key)) return;
      this.views.add(key);
    }
    if (this.pending.length >= 100) return;
    this.pending.push({
      eventId,
      type,
      receipt,
      generation: this.generation,
      id: this.document.defaultView?.crypto.randomUUID() || crypto.randomUUID(),
    });
    this.flush(eventId);
  }
  leaveEvent(): void {
    this.activeEvent = undefined;
    this.pending = this.pending.filter(
      (entry) => entry.type !== "event_viewed",
    );
    this.waiting = this.waiting.filter(
      (entry) => entry.type !== "event_viewed",
    );
  }
  private allowed(observation: Observation): boolean {
    return (
      this.measurement.analytics &&
      this.measurement.trackingReceipt === observation.receipt &&
      this.generation === observation.generation
    );
  }
  private flush(eventId: number): void {
    const token = this.context.tokenFor(eventId);
    if (!token) return;
    const entries = this.pending.filter((entry) => entry.eventId === eventId);
    this.pending = this.pending.filter((entry) => entry.eventId !== eventId);
    for (const entry of entries) {
      if (!this.allowed(entry)) continue;
      if (entry.type === "event_viewed")
        this.views.add(`${entry.receipt}:${eventId}:${token}`);
      this.stream.next(entry);
    }
  }
}
